/**
 * A row from the rows endpoint, as the shared board row renders it.
 *
 * `TrackerRow` keeps a record's own fields apart from the board's custom
 * columns; `BoardRowItem` reads every cell out of one map. The two key spaces
 * cannot collide - a record field id is a name its source declares, a custom
 * column id is a uuid - so merging them is what lets a project render through
 * the same row component as a brief, cell renderers and all.
 *
 * The per-source behaviour is tabled rather than branched, for the same reason
 * `record-fields` tables its columns: a second source adds a row here and
 * touches nothing else.
 */
import { format } from 'date-fns';

import type { TrackerRow } from '@/entities/tracker';
import { ROUTES } from '@/shared/config/routes';

import type { BoardRowItem } from '../ui/TrackersBriefRow';

import { WORKSPACE_FIELD } from './record-fields';

type RecordFields = Record<string, unknown>;

/**
 * Project statuses that mean the work is finished. One of these past its
 * deadline is done rather than late, and flagging it would teach people to
 * ignore the flag on the rows that are.
 */
// eslint-disable-next-line i18next/no-literal-string -- wire enum values, not copy
const SETTLED_PROJECT_STATUSES: readonly string[] = ['live', 'signed_off'];

/** The calendar day a date or timestamp falls on, in the value's own terms. */
function dayOf(value: unknown): string | null {
  return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : null;
}

/**
 * Late is decided on `YYYY-MM-DD` text rather than on instants. Both sides are
 * already that shape and its string order is its date order, so nothing has to
 * rule on which timezone a deadline of "the 3rd" was meant in. A deadline of
 * today is not yet late, matching the `overdue` filter bucket.
 */
function isProjectLate(fields: RecordFields, today: string): boolean {
  const deadline = dayOf(fields.deadline);
  if (!deadline) return false;
  const status = fields.status;
  if (typeof status === 'string' && SETTLED_PROJECT_STATUSES.includes(status)) return false;
  return deadline < today;
}

/**
 * `Partial` is what makes the lookup honest: an index signature otherwise types
 * every key as present, and a board on a source with no entry here reads the
 * miss as a function.
 */
const LATE_BY_SOURCE: Readonly<
  Partial<Record<string, (fields: RecordFields, today: string) => boolean>>
> = {
  projects: isProjectLate,
};

/** A route segment is only usable when the source actually projected it. */
function idOf(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/**
 * Project routes are scoped to the platform id, which the source projects into
 * `fields`. `rowKey` is the internal id and 403s against them.
 */
function projectHref(row: TrackerRow): string | null {
  const platformProjectId = idOf(row.fields.platformProjectId);
  return platformProjectId ? ROUTES.PROJECT_DETAIL(platformProjectId) : null;
}

/**
 * An asset lives inside a folder inside a project, so its route needs all three
 * ids. The row key is the file id; the other two come off the projections the
 * assets source adds for exactly this.
 */
function assetHref(row: TrackerRow): string | null {
  const platformProjectId = idOf(row.fields.platformProjectId);
  const folderId = idOf(row.fields.folderId);
  return platformProjectId && folderId
    ? ROUTES.PROJECT_FILE_DETAIL(platformProjectId, folderId, row.rowKey)
    : null;
}

const HREF_BY_SOURCE: Readonly<Partial<Record<string, (row: TrackerRow) => string | null>>> = {
  projects: projectHref,
  assets: assetHref,
};

/**
 * Whether this source has a destination at all, as against a row that happens
 * to be missing the id its route needs. A board answering `false` wires no row
 * click, so the rows do not offer a cursor and a hover state for a navigation
 * that cannot happen.
 */
export function hasRecordRoute(itemSource: string | null | undefined): boolean {
  return HREF_BY_SOURCE[itemSource ?? ''] !== undefined;
}

/** Where a row's record lives, or null while the board has no route to it. */
export function recordRowHref(
  row: TrackerRow,
  itemSource: string | null | undefined,
): string | null {
  return HREF_BY_SOURCE[itemSource ?? '']?.(row) ?? null;
}

/**
 * Which file a row's record IS, for the sources whose records are files.
 *
 * Tabled beside the routes rather than read off a `files` field, because "this
 * row has a picture" and "this row is a file the files endpoint will answer
 * for" are different claims. The assets source satisfies the second one by the
 * same fact `assetHref` leans on - its row key is the file id - and a source
 * that merely carries a file field satisfies only the first, so asking the
 * endpoint for its row key would fetch somebody else's file.
 */
const FILE_ID_BY_SOURCE: Readonly<Partial<Record<string, (row: TrackerRow) => string | null>>> = {
  assets: (row) => row.rowKey,
};

/**
 * Whether this source's records are files at all, as against a row that happens
 * to be missing the id a preview needs. A board answering `false` wires no
 * preview, so its thumbnail cells keep the row's own destination.
 */
export function hasRecordFile(itemSource: string | null | undefined): boolean {
  return FILE_ID_BY_SOURCE[itemSource ?? ''] !== undefined;
}

/** The file a row's record is, or null when the source's records are not files. */
export function recordRowFileId(
  row: TrackerRow,
  itemSource: string | null | undefined,
): string | null {
  return FILE_ID_BY_SOURCE[itemSource ?? '']?.(row) ?? null;
}

/**
 * The row's record lives in another workspace than the board's. Only a board
 * reading several workspaces carries a row's workspace, so a row without one
 * is the board's own.
 */
export function isForeignRow(row: TrackerRow, boardWorkspaceId: string): boolean {
  const workspace = row.fields[WORKSPACE_FIELD.id];
  return typeof workspace === 'string' && workspace !== '' && workspace !== boardWorkspaceId;
}

export interface RecordRowOptions {
  itemSource: string | null | undefined;
  /** The field whose value names the row in the Item column. */
  titleField: string | null;
  /** Stands in when the record has no name, so the row is still reachable. */
  untitledLabel: string;
  /** One instant for the whole board; see `useRecordBoard`. */
  now: Date;
  /** The workspace the board belongs to, which decides whose rows are foreign. */
  boardWorkspaceId: string;
  /** Names the workspace cell, which the row carries as an id. */
  workspaceLabel: (id: string) => string;
}

export function toBoardRowItems(
  rows: readonly TrackerRow[],
  options: RecordRowOptions,
): BoardRowItem[] {
  const { itemSource, titleField, untitledLabel, now, boardWorkspaceId, workspaceLabel } = options;
  const isLate = LATE_BY_SOURCE[itemSource ?? ''];
  const today = format(now, 'yyyy-MM-dd');

  return rows.map((row) => {
    const title = titleField ? row.fields[titleField] : null;
    const workspace = row.fields[WORKSPACE_FIELD.id];
    return {
      id: row.rowKey,
      title: typeof title === 'string' && title ? title : untitledLabel,
      // The board's own columns win a collision, which only a source that named
      // a field with a uuid could produce.
      customColumnValues: {
        ...row.fields,
        ...(typeof workspace === 'string'
          ? { [WORKSPACE_FIELD.id]: workspaceLabel(workspace) }
          : {}),
        ...row.customColumnValues,
      },
      customColumnMeta: row.customColumnMeta ?? null,
      overdue: isLate?.(row.fields, today) ?? false,
      // Per row, not per board: an asset sitting at the project root has no
      // folder in its route, so its link cannot be built though every other
      // row on the same board has one. Without this the row still draws a
      // pointer and announces "Open <name>" on a click that goes nowhere.
      routable: recordRowHref(row, itemSource) !== null,
      // Comment threads are kept per workspace, and the board's endpoint
      // answers only for its own.
      commentable: !isForeignRow(row, boardWorkspaceId),
    };
  });
}
